import { Link, useNavigate } from 'react-router-dom';
import { FormEvent, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useThemeColor } from '../../hooks/useThemeColor';
import { login } from '../../api/Auth';
import { storeToken } from '../../utils/Auth';
import { useAnalytics } from '../../analytics/useAnalytics';
import { useLocalizedText } from '../../hooks/useLocalizedText';
import { useSelfAffiliateCode } from '../../hooks/useSelfAffiliateCode';

export const LoginForm = () => {
  const t = useLocalizedText();

  const navigate = useNavigate();
  const { primary, secondary } = useThemeColor();
  const { identify } = useAnalytics();
  const { refresh: refreshSelfAffiliateCode } = useSelfAffiliateCode();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = (ev: FormEvent) => {
    ev.preventDefault();

    setLoading(true);
    login(email, password)
      .then((token) => {
        if (token) {
          toast.success(t('login.success'));
          storeToken(token);
          identify({ email });
          refreshSelfAffiliateCode();
          navigate('/');
        } else {
          toast.info(
            'Güvenlik sebebiyle şifrenizi sıfırlamanız gerekiyor. Talimatlar E-Posta adresinize gönderilmiştir.',
          );
        }
      })
      .catch((err) => {
        toast.error(err.message);
      })
      .finally(() => setLoading(false));
  };

  return (
    <Paper
      component='form'
      onSubmit={handleLogin}
      elevation={3}
      sx={{
        px: 2.5,
        py: 2,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        height: '100%',
      }}
    >
      <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
        {t('login.login')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          width: '100%',
        }}
      >
        <Typography>{t('login.email')}</Typography>

        <TextField
          size='small'
          fullWidth
          disabled={loading}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          width: '100%',
        }}
      >
        <Typography>{t('login.password')}</Typography>
        <TextField
          size='small'
          fullWidth
          disabled={loading}
          type={showPassword ? undefined : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Link
          to='/sifremi-unuttum'
          style={{
            textDecoration: 'none',
            color: 'inherit',
            cursor: 'pointer',
          }}
        >
          <Typography>{t('login.forgot_password')}</Typography>
        </Link>

        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                size='small'
                checked={showPassword}
                onChange={() => setShowPassword((x) => !x)}
              />
            }
            label={
              <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                {t('login.show_password')}
              </Typography>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                size='small'
                checked={rememberMe}
                onChange={() => setRememberMe((x) => !x)}
              />
            }
            label={
              <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                {t('login.remember_me')}
              </Typography>
            }
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          alignItems: 'center',
        }}
      >
        <Button
          variant='contained'
          type='submit'
          disabled={loading}
          sx={{
            textTransform: 'none',
            backgroundColor: primary,
            color: secondary,
          }}
        >
          {t('login.login')}
        </Button>

        {loading && <CircularProgress color='primary' />}
      </Box>
    </Paper>
  );
};
